'use client';

import { useGetMe } from 'Shared/hooks/api';
import { useTokenStore } from 'Shared/hooks/useTokenStore';
import { LINK, SIZE } from '@constants';
import { Column, getColorValue, Icon, Row, Typography, useTheme } from '@viewup/ui';
import { EmptyViewingMoneyModal } from 'app/_components/EmptyViewingMoneyModal';
import { useGlobalModalStore, useIsBottomBarVisible } from 'app/_hook';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import type { MouseEventHandler } from 'react';
import { bottomBarMenu } from './BottomBar.const';

export const BottomBar = () => {
  const router = useRouter();
  const theme = useTheme();
  const pathname = usePathname();
  const bottomBarVisible = useIsBottomBarVisible();
  const { data } = useGetMe();
  const { accessToken } = useTokenStore();

  const { getValue } = useGlobalModalStore();
  const { setConfirmModal, setAlertModal } = getValue('actions');

  const isActive = (link: string) => {
    if (link === LINK.HOME) {
      return pathname === LINK.HOME;
    }

    return pathname.includes(LINK.VIEWING_MONEY || LINK.VIEWING_MONEY_LIST)
      ? link.startsWith(LINK.VIEWING_MONEY || LINK.VIEWING_MONEY_LIST)
      : pathname.startsWith(link);
  };

  const checkLoggedIn: MouseEventHandler = (e) => {
    if (!accessToken) {
      e.preventDefault();

      setConfirmModal({
        content: `본 서비스는 회원만 이용이 가능해요.\n로그인할까요?`,
        onClickFillButton: () => {
          router.replace(LINK.LOGIN);
        },
      });

      return false;
    }
  };

  const checkViewingMoney: MouseEventHandler = (e) => {
    e.preventDefault();

    EmptyViewingMoneyModal(data?.data.totalPoint || 0, router, setAlertModal);
  };

  if (!bottomBarVisible) return null;

  return (
    <Row
      alignItems='center'
      css={{
        borderTop: `1px solid ${getColorValue('border.tertiary', theme)}`,
        height: `calc(${SIZE.BOTTOM_BAR_HEIGHT}px + env(safe-area-inset-bottom))`,
        paddingBottom: 'calc(env(safe-area-inset-bottom))',
      }}
      justifyContent='space-around'
    >
      {bottomBarMenu.map(({ gaLabel, title, icon, link, requireLogin }) => (
        <Link
          href={link}
          key={link}
          {...(requireLogin && {
            onClick: accessToken && link === LINK.VIEWING_MONEY ? checkViewingMoney : checkLoggedIn,
          })}
          className={`ga-link-bottombar-${gaLabel}`}
        >
          <Column alignItems='center' gap={4} justifyContent='center'>
            <Icon
              color={isActive(link) ? 'icon.interactive.primary' : 'icon.tertiary'}
              size='XS'
              source={icon}
            />
            <Typography
              color={isActive(link) ? 'text.interactive.primary' : 'text.tertiary'}
              fontType='caption.regular'
            >
              {title}
            </Typography>
          </Column>
        </Link>
      ))}
    </Row>
  );
};
